import { useMemo } from 'react'
import { BackgroundNoteForm } from '../../features/edit-supporter/BackgroundNoteForm'
import EngagementLevelForm from '../../features/edit-supporter/EngagementLevelForm'
import TagsForm from '../../features/edit-supporter/TagsForm'
import TagFiltersForm from '../../features/edit-supporter/TagFiltersForm'
import { FederalDistrictForm } from '../../features/edit-supporter/FederalDistrictForm'
import ContactDetailsForm from '../../features/edit-supporter/ContactDetailsForm'
import AddressForm from '../../features/edit-supporter/AddressForm'
import { scopedTranslation } from '@utils/I18n'

import type { EngagementLevelsIndexQueryType } from '@frontend/graphql/queries/engagementLevels'
import type { TagFiltersIndexQueryType } from '@frontend/graphql/queries/tagFilters'
import type { SupporterPermissions } from '../SupporterTabs'

const t = scopedTranslation('supporter_show')

export type SupporterDetailsType = {
  id: string
  firstName?: string | null
  lastName?: string | null
  fullName?: string | null
  tags?: { nodes: Array<{ name: string }> }
  note?: string | null
  engagementLevel?: string | null
  email?: string | null
  primaryMobile?: string | null
  primaryPhone?: string | null
  federalDistrict?: string | null
  address1?: string | null
  address2?: string | null
  city?: string | null
  zip?: string | null
  state?: string | null
  countryCode?: string | null
  country?: string | null
}

type SupporterDetailsTabProps = {
  supporter: SupporterDetailsType
  engagementLevels: EngagementLevelsIndexQueryType['engagementLevels']
  tagFilters?: TagFiltersIndexQueryType['tagFilters']
  supporterTagFilters?: Array<{ value: string; label: string }>
  permissions: Partial<SupporterPermissions>
  groupId?: string
}

export function SupporterDetailsTab({
  supporter,
  engagementLevels,
  tagFilters,
  supporterTagFilters,
  permissions,
  groupId
}: SupporterDetailsTabProps) {
  const mappedTags = useMemo(
    () => (supporter?.tags?.nodes ?? []).map((tag) => ({ value: tag.name, label: tag.name })),
    [supporter?.tags?.nodes]
  )

  if (!supporter) {
    return <div>{t('no_supporter_data_available')}</div>
  }

  return (
    <div className="tw:flex tw:flex-col tw:gap-4">
      <BackgroundNoteForm
        supporterId={supporter.id}
        initialNote={supporter.note ?? null}
      />

      <EngagementLevelForm
        supporterId={supporter.id}
        engagementLevel={supporter.engagementLevel}
        engagementLevels={engagementLevels.nodes || []}
        canEdit={permissions.updateEngagementLevel ?? false}
      />

      {permissions.showAllTags && permissions.readTags && (
        <TagsForm
          groupId={groupId}
          supporterId={supporter.id}
          updateTags={permissions.updateTags ?? false}
          tags={mappedTags}
        />
      )}

      {groupId && tagFilters && (
        <TagFiltersForm
          groupId={groupId}
          supporterId={supporter.id}
          tagFilters={tagFilters.nodes || []}
          canEdit={permissions.updateTagFilters ?? false}
          supporterTagFilters={supporterTagFilters ?? []}
        />
      )}

      {(permissions.electoralFiltersEnabled || permissions.editFederalDistrict) && (
        <FederalDistrictForm
          supporterId={supporter.id}
          initialFederalDistrict={supporter.federalDistrict ?? null}
          editFederalDistrict={permissions.editFederalDistrict ?? false}
        />
      )}

      <ContactDetailsForm
        supporterId={supporter.id}
        firstName={supporter.firstName ?? null}
        lastName={supporter.lastName ?? null}
        email={supporter.email ?? ''}
        mobile={supporter.primaryMobile ?? ''}
        callPhone={supporter.primaryPhone ?? ''}
        canEdit={permissions.updateDetails ?? false}
      />

      <AddressForm
        supporterId={supporter.id}
        address1={supporter.address1 ?? ''}
        address2={supporter.address2 ?? ''}
        city={supporter.city ?? ''}
        zip={supporter.zip ?? ''}
        state={supporter.state ?? ''}
        countryCode={supporter.countryCode ?? ''}
        country={supporter.country ?? ''}
        canEdit={permissions.updateDetails ?? false}
        showAddress2={permissions.showAddress2 ?? false}
      />
    </div>
  )
}